"use client"

import { css, cx } from "@emotion/css"
import styled from "@emotion/styled"
import React, { useRef } from "react"

import { useCornerTapFlip } from "@/hooks/course-material/useCornerTapFlip"
import { respondToOrLarger } from "@/shared-module/common//styles/respond"
import { baseTheme, headingFont } from "@/shared-module/common/styles"
import { INCLUDE_THIS_HEADING_IN_HEADINGS_NAVIGATION_CLASS } from "@/shared-module/common/utils/constants"
import { COURSE_MATERIAL_DEFAULT_BLOCK_MARGIN_REM } from "@/utils/course-material/constants"
import { escapeUrlForCss } from "@/utils/course-material/sanitizeCourseMaterialHtml"

import {
  type HeroBackgroundImages,
  heroTextReadabilityCss,
  resolveHeroBackgroundImages,
} from "./heroBackgroundImages"
import ParsedText from "./ParsedText"

interface TextBoxProps {
  fontColor?: string
  direction: string
  backgroundImages: HeroBackgroundImages
}

const CENTERED_MARGIN = "0 auto"
const DEFAULT_MARGIN_MEDIUM_SCREEN = "0 0 0 45%"
const DEFAULT_MARGIN_LARGE_SCREEN = "0 0 0 35%"

const TextBox = styled.div<TextBoxProps>`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  padding: 2rem 2.5rem 3rem 2.5rem;
  margin: 0 auto;
  text-align: ${({ direction }) => direction};
  align-items: ${({ direction }) => direction};
  justify-content: center;
  ${({ backgroundImages }) => heroTextReadabilityCss(backgroundImages.mobile)}

  ${respondToOrLarger.md} {
    margin: ${({ direction }) =>
      direction === "center" ? CENTERED_MARGIN : DEFAULT_MARGIN_MEDIUM_SCREEN};
    ${({ backgroundImages }) => heroTextReadabilityCss(backgroundImages.medium)}
  }

  ${respondToOrLarger.lg} {
    margin: ${({ direction }) =>
      direction === "center" ? CENTERED_MARGIN : DEFAULT_MARGIN_LARGE_SCREEN};
    ${({ backgroundImages }) => heroTextReadabilityCss(backgroundImages.large)}
  }

  ${respondToOrLarger.xl} {
    ${({ backgroundImages }) => heroTextReadabilityCss(backgroundImages.xlarge)}
  }

  h1 {
    font-weight: 700;
    z-index: 20;
    margin-bottom: 0.5rem;
    line-height: 120%;
    color: ${({ color }) => (color ? color : baseTheme.colors.gray[700])};
  }

  .chapter {
    font-size: 18px;
    color: ${({ color }) => (color ? color : baseTheme.colors.gray[700])};
    opacity: 0.9;
    text-align: ${({ direction }) => direction};
    font-weight: 500;
    font-family: ${headingFont};
    margin-bottom: 0.2rem;
    text-transform: capitalize;
  }

  span {
    color: ${({ color }) => (color ? color : baseTheme.colors.gray[700])};
    font-size: 18px;
    opacity: 0.9;
    z-index: 20;
  }
`
export interface HeroSectionProps {
  subtitle: string
  title: string
  bg?: string
  backgroundImage?: string
  backgroundImageMedium?: string
  backgroundImageLarge?: string
  backgroundImageXLarge?: string
  fontColor?: string
  alignCenter: boolean
  alignBottom?: boolean | undefined
  backgroundColor?: string
  label?: string
  useDefaultTextForLabel?: boolean
  partiallyTransparent?: boolean
  backgroundRepeatX?: boolean
  backgroundSizeRem?: number
}

export type CardProps = React.HTMLAttributes<HTMLDivElement> & HeroSectionProps

const HeroSection: React.FC<React.PropsWithChildren<CardProps>> = ({
  title,
  subtitle,
  backgroundImage,
  backgroundImageMedium,
  backgroundImageLarge,
  backgroundImageXLarge,
  fontColor,
  alignCenter,
  backgroundColor,
  label,
  partiallyTransparent,
  backgroundRepeatX,
  backgroundSizeRem,
  alignBottom,
}) => {
  const CENTER = "center"
  const LEFT = "left"
  const direction = alignCenter ? CENTER : LEFT
  // oxlint-disable-next-line i18next/no-literal-string
  const backgroundVerticalAlignment = alignBottom ? "bottom" : "center"
  const { containerRef, onPointerDown, flipClassName } = useCornerTapFlip()
  const labelRef = useRef<HTMLElement>(null)

  const backgroundImages = resolveHeroBackgroundImages({
    backgroundImage,
    backgroundImageMedium,
    backgroundImageLarge,
    backgroundImageXLarge,
  })

  return (
    <div
      id="hero-section"
      ref={containerRef}
      onPointerDown={onPointerDown}
      className={css`
        width: 100%;
        border-radius: 1px;
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
        padding: 7.5em 1em;
        margin-bottom: 3rem;
        /** There should not be empty space before the hero section, so we'll undo the default block margin **/
        margin-top: -${COURSE_MATERIAL_DEFAULT_BLOCK_MARGIN_REM}rem;
        background-color: ${backgroundColor || baseTheme.colors.clear[100]};
        position: relative;
        touch-action: manipulation;

        &::after {
          background-size: ${backgroundSizeRem ?? 26}rem;
          width: 100%;
          height: 100%;
          content: "";
          opacity: 0.3;
          background-image: url("${escapeUrlForCss(backgroundImages.mobile)}");
          background-repeat: ${backgroundRepeatX ? "repeat-x" : "no-repeat"};
          background-position: center ${backgroundVerticalAlignment};
          position: absolute;
          top: 0px;
          left: 0px;

          ${respondToOrLarger.md} {
            opacity: ${partiallyTransparent ? "1" : "0.4"};
            background-position: ${direction} ${backgroundVerticalAlignment};
            background-size: ${direction === "center" ? "contain" : "22rem"};
            left: ${direction === "center" ? "0" : "30px"};
            background-image: url("${escapeUrlForCss(backgroundImages.medium)}");
          }

          ${respondToOrLarger.lg} {
            opacity: ${partiallyTransparent ? "1" : "0.4"};
            background-position: ${direction} ${backgroundVerticalAlignment};
            background-size: ${direction === "center" ? "contain" : "26rem"};
            left: ${direction === "center" ? "0" : "40px"};
            background-image: url("${escapeUrlForCss(backgroundImages.large)}");
          }

          ${respondToOrLarger.xl} {
            background-image: url("${escapeUrlForCss(backgroundImages.xlarge)}");
          }
        }
      `}
    >
      <TextBox backgroundImages={backgroundImages} color={fontColor} direction={direction}>
        {/* No wrapper: the label must stay a flex item for its margin to apply. */}
        <ParsedText
          text={label}
          tag="span"
          // oxlint-disable-next-line i18next/no-literal-string
          tagProps={{ className: "chapter" }}
          options={{ glossary: false }}
          useWrapperElement={false}
          wrapperRef={labelRef}
        />
        <ParsedText
          text={title}
          tag="h1"
          tagProps={{
            className: cx(INCLUDE_THIS_HEADING_IN_HEADINGS_NAVIGATION_CLASS, flipClassName),
          }}
          options={{ glossary: false }}
          useWrapperElement={true}
        />
        <ParsedText
          text={subtitle}
          tag="span"
          options={{ glossary: false }}
          useWrapperElement={true}
        />
      </TextBox>
    </div>
  )
}

export default HeroSection
